import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, TrendingUp, Users, Check, ArrowRight } from 'lucide-react';

/**
 * Onaylı olmayan davet evleri için otomatik açılan modal.
 *
 * DAVRANIŞ (Güvenli Zorunlu Mod):
 * - Yalnızca gerçek kullanıcıya render edilir (isBot=true ise null).
 * - Sayfa yüklendikten 4 sn sonra otomatik açılır.
 * - Küçük X butonu vardır (Google "Intrusive Interstitial" cezasından kaçınmak için).
 * - Kapatılırsa 3 sn sonra otomatik olarak yeniden açılır → kullanıcı iş ortağı
 *   formuna yönlenmeye teşvik edilir.
 * - Aynı IP başka bir onaysız mekana girdiğinde de aynı döngü çalışır çünkü
 *   sessionStorage/localStorage flag KULLANMIYORUZ. Her onaysız mekan girişinde loop yeniden başlar.
 *
 * Cloaking değil çünkü: HTML kaynağı bot & kullanıcı için aynı — sadece
 * kullanıcının tarayıcısı JS ile modal'ı ekrana getiriyor.
 */
const UnclaimedAutoModal = React.memo(({ venue, isBot }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const reopenTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const navigatedRef = useRef(false);

  // İlk açılış (4 sn sonra)
  useEffect(() => {
    if (isBot || !venue?.slug) return;
    initialTimerRef.current = setTimeout(() => {
      setOpen(true);
    }, 4000);
    return () => {
      if (initialTimerRef.current) clearTimeout(initialTimerRef.current);
      if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
    };
  }, [isBot, venue?.slug]);

  // Body scroll kilidi
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Kapat → 3 sn sonra yeniden aç (kullanıcı partner'a gitmediyse)
  const closeAndReopen = () => {
    setOpen(false);
    if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
    reopenTimerRef.current = setTimeout(() => {
      if (!navigatedRef.current) setOpen(true);
    }, 3000);
  };

  // ESC tuşu ile kapatma da aynı loop'a girsin
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeAndReopen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (isBot || !venue) return null;
  if (!open) return null;

  const goPartner = () => {
    navigatedRef.current = true;
    if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
    setOpen(false);
    navigate(`/isortagi-basvuru/${venue.slug}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={closeAndReopen}
      data-testid="unclaimed-auto-modal"
    >
      <div
        className="relative max-w-lg w-full rounded-3xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Kapat"
          onClick={closeAndReopen}
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/95 hover:bg-white shadow ring-1 ring-neutral-200 flex items-center justify-center transition"
          data-testid="unclaimed-modal-close"
        >
          <X className="h-4 w-4 text-neutral-700" />
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 p-8 text-white text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-4">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight drop-shadow">
            {venue.name} <span className="italic">size mi ait?</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/95 drop-shadow">
            Bu sayfa her ay yüzlerce potansiyel müşteriye ulaşıyor.
            <br />İşletme sahibiyseniz, hemen sahiplenip talepleri kaçırmayın.
          </p>
        </div>

        {/* Benefits */}
        <div className="p-6 sm:p-7">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="mx-auto h-10 w-10 rounded-xl bg-orange-50 ring-1 ring-orange-100 flex items-center justify-center text-orange-600 mb-1.5">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div className="text-[11px] font-semibold text-neutral-900">Daha çok müşteri</div>
            </div>
            <div>
              <div className="mx-auto h-10 w-10 rounded-xl bg-pink-50 ring-1 ring-pink-100 flex items-center justify-center text-pink-600 mb-1.5">
                <Users className="h-5 w-5" />
              </div>
              <div className="text-[11px] font-semibold text-neutral-900">Direkt teklif</div>
            </div>
            <div>
              <div className="mx-auto h-10 w-10 rounded-xl bg-purple-50 ring-1 ring-purple-100 flex items-center justify-center text-purple-600 mb-1.5">
                <Check className="h-5 w-5" />
              </div>
              <div className="text-[11px] font-semibold text-neutral-900">15 gün ücretsiz Pro</div>
            </div>
          </div>

          <ul className="mt-5 space-y-2 text-[13px] text-neutral-700">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Kendi bilgi &amp; görsellerinizi anında güncelleyebilirsiniz</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Fiyat teklifleri direkt size ulaşır, aracı yok</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> 2 dakikada doğrulama, 15 gün ücretsiz Pro paket</li>
          </ul>

          <button
            type="button"
            onClick={goPartner}
            className="mt-6 w-full h-12 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold text-[15px] shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition inline-flex items-center justify-center gap-2"
            data-testid="unclaimed-modal-cta"
          >
            Sahiplen · 15 Gün Ücretsiz
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

UnclaimedAutoModal.displayName = 'UnclaimedAutoModal';
export default UnclaimedAutoModal;
